/**
 * Click and drag inside a scroller to scroll it sideways: the way to a wide
 * table's far columns without going down to its scrollbar. For the mouse
 * only -- a touch screen pans by itself -- and a press that barely moves is
 * still a click.
 *
 * @param {HTMLElement} scroller  the element that scrolls
 * @param {{ignore?: string}} options  `ignore`: a selector for where a press
 *   is left alone, on top of form controls and links (a table's header row,
 *   a row's drag handle...)
 */

const THRESHOLD = 5;

const enableDragScroll = (scroller, { ignore = '' } = {}) => {

  if (!scroller) {
    return;
  }

  const leaveAlone = ['input', 'button', 'select', 'textarea', 'label', 'a', ignore]
    .filter(Boolean)
    .join(', ');

  let start = null;
  let panning = false;

  const onMouseMove = e => {

    if (!start) return;

    const dx = e.clientX - start.x;

    if (!panning) {
      if (Math.abs(dx) < THRESHOLD) return;

      panning = true;
      scroller.classList.add('is-panning');

      // whatever the press began to select
      const selection = window.getSelection ? window.getSelection() : null;
      if (selection) selection.removeAllRanges();
    }

    e.preventDefault();
    scroller.scrollLeft = start.scrollLeft - dx;
  };

  const onMouseUp = () => {

    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);

    if (panning) {
      scroller.classList.remove('is-panning');

      // the click that ends a drag isn't one: keep it from whatever is under it
      const swallow = e => {
        e.stopPropagation();
        e.preventDefault();
      };

      scroller.addEventListener('click', swallow, true);
      setTimeout(() => scroller.removeEventListener('click', swallow, true), 0);
    }

    start = null;
    panning = false;
  };

  scroller.addEventListener('mousedown', e => {

    // the main button, and only with somewhere to scroll to
    if (e.button !== 0 || scroller.scrollWidth <= scroller.clientWidth) return;

    if (e.target.closest(leaveAlone)) return;

    // the scrollbars report the scroller itself
    if (e.target === scroller && (e.offsetX >= scroller.clientWidth || e.offsetY >= scroller.clientHeight)) return;

    start = { x: e.clientX, scrollLeft: scroller.scrollLeft };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  });

};

export default enableDragScroll;
